"use client";

import { c as _c } from "react/compiler-runtime";
import { IconArrowRightOutline18, IconCheckOutline18, IconChevronRightOutline18, IconWrenchOutline18 } from "nucleo-ui-outline-18";
/**
 * ErrorFieldEditor
 *
 * Shows a fixable field with current → suggested value and a tick button to apply.
 * Styled to match the sidebar form fields for visual consistency.
 */

import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { useCallback, useState } from "react";
import type { FixableField } from "./errorAnalysisCache";

/** Stringify a value for display — uses JSON.stringify for objects/arrays, String() for primitives */
function displayValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

/** Format dot-notation field names readably: "headers.Authorization" → ["headers", "Authorization"] */
function formatFieldName(name: string): React.ReactNode {
  const parts = name.split(".");
  if (parts.length === 1) return <span>{name}</span>;
  return <span className="inline-flex items-center gap-0.5">
      {parts.map((part, i) => <span key={i} className="inline-flex items-center gap-0.5">
          {i > 0 && <IconChevronRightOutline18 className="text-ds-text-disabled dark:text-ds-text-muted h-2.5 w-2.5" />}
          <span>{part}</span>
        </span>)}
    </span>;
}
interface ErrorFieldEditorProps {
  field: FixableField;
  onApplyFix: (fieldName: string, value: unknown) => void;
  onAppliedChange?: (fieldName: string, applied: boolean) => void;
  /** Start in applied state (persisted across re-renders by parent) */
  initialApplied?: boolean;
  className?: string;
}
export function ErrorFieldEditor({
  field,
  onApplyFix,
  onAppliedChange,
  initialApplied,
  className
}: ErrorFieldEditorProps) {
  const [editValue, setEditValue] = useState(displayValue(field.suggestedValue));
  const [applied, setApplied] = useState(initialApplied ?? false);
  const [appliedValue, setAppliedValue] = useState<string | null>(null);
  const handleApply = useCallback(() => {
    let value: unknown = editValue;
    // Try to parse JSON so objects/arrays are applied correctly.
    // Only use the parsed result for objects/arrays — keep primitives (numbers,
    // booleans) as strings since node fields like headers expect string values.
    if (typeof value === "string") {
      try {
        const parsed = JSON.parse(value);
        if (typeof parsed === "object" && parsed !== null) {
          value = parsed;
        }
      } catch {
        /* keep as string */
      }
    }
    onApplyFix(field.fieldName, value);
    setApplied(true);
    setAppliedValue(editValue);
    onAppliedChange?.(field.fieldName, true);
  }, [field, editValue, onApplyFix, onAppliedChange]);
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setEditValue(newValue);
    // Revert to Apply button if user edits the field after applying
    if (applied && newValue !== appliedValue) {
      setApplied(false);
      onAppliedChange?.(field.fieldName, false);
    }
  }, [applied, appliedValue, field.fieldName, onAppliedChange]);
  const currentDisplay = displayValue(field.currentValue);
  const hasCurrentValue = currentDisplay !== "";
  return <div className={cn("rounded-lg border p-3", applied ? "border-green-200 bg-green-50/50 dark:border-green-900/50 dark:bg-green-950/20" : "border-ds-neutral-200 dark:border-ds-neutral-700/50 dark:bg-ds-neutral-800/30 bg-zinc-50/50", className)}>
      {/* Label + explanation */}
      <div className="mb-2">
        <label className="text-ds-text-secondary text-xs font-medium">{formatFieldName(field.fieldName)}</label>
        {field.explanation && <p className="text-ds-text-muted text-2xs mt-0.5 leading-tight">{field.explanation}</p>}
      </div>
      {/* Current → Suggested layout */}
      {hasCurrentValue && <div className="mb-2 flex items-center gap-1.5">
          <span className="bg-ds-neutral-100 text-ds-text-muted dark:bg-ds-neutral-800/80 dark:text-ds-text-muted text-2xs min-w-0 flex-1 truncate rounded-md px-2 py-1 font-mono">
            {currentDisplay}
          </span>
          <IconArrowRightOutline18 className="text-ds-text-disabled h-3 w-3 shrink-0" />
        </div>}
      {/* Editable suggested value + apply button */}
      <div className="flex items-center gap-1.5">
        <Input type="text" value={editValue} onChange={handleChange} className={cn("text-2xs min-w-0 flex-1 px-2 py-1 font-mono transition-colors focus:ring-1", applied ? "border-green-300 bg-green-50 text-green-800 focus:ring-green-400 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300 dark:focus:ring-green-700" : "border-ds-neutral-300 focus:border-blue-400 focus:ring-blue-400/30 dark:border-zinc-600 dark:focus:ring-blue-500/30")} onKeyDown={e_0 => {
        if (e_0.key === "Enter") handleApply();
      }} />
        <button onClick={handleApply} disabled={applied} title={applied ? "Applied" : "Apply fix"} className={cn("text-2xs flex shrink-0 items-center gap-1 rounded-md px-2 py-1 font-medium transition-all duration-200", applied ? "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400" : "bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-400")}>
          {applied ? <IconCheckOutline18 className="h-3 w-3 scale-110 transition-transform duration-200" /> : "Apply"}
        </button>
      </div>
    </div>;
}
interface ErrorFieldEditorListProps {
  fields: FixableField[];
  onApplyFix: (fieldName: string, value: unknown) => void;
  /** Set of field names already applied (persisted by parent across re-renders) */
  initialAppliedFields?: Set<string>;
  /** Notify parent when applied fields change */
  onAppliedFieldsChange?: (fields: Set<string>) => void;
  className?: string;
}
export function ErrorFieldEditorList(t0) {
  const $ = _c(25);
  const {
    fields,
    onApplyFix,
    initialAppliedFields,
    onAppliedFieldsChange,
    className
  } = t0;
  let t1;
  if ($[0] !== initialAppliedFields) {
    t1 = initialAppliedFields ?? new Set();
    $[0] = initialAppliedFields;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const [appliedFields, setAppliedFields] = useState(t1);
  let t2;
  if ($[2] !== onAppliedFieldsChange) {
    t2 = (fieldName, applied) => {
      setAppliedFields(prev => {
        const next = new Set(prev);
        if (applied) {
          next.add(fieldName);
        } else {
          next.delete(fieldName);
        }
        onAppliedFieldsChange?.(next);
        return next;
      });
    };
    $[2] = onAppliedFieldsChange;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleAppliedChange = t2;
  if (fields.length === 0) {
    return null;
  }
  let t3;
  if ($[4] !== className) {
    t3 = cn("space-y-2", className);
    $[4] = className;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <IconWrenchOutline18 className="text-ds-text-disabled dark:text-ds-text-muted h-3.5 w-3.5" />;
    t5 = <h4 className="text-ds-text-subtle text-xs font-semibold">Suggested Fixes</h4>;
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  let t6;
  if ($[8] !== fields.length) {
    t6 = <div className="flex items-center gap-1.5">{t4}{t5}<Badge className="px-1.5 leading-none">{fields.length}</Badge></div>;
    $[8] = fields.length;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== appliedFields || $[11] !== fields || $[12] !== handleAppliedChange || $[13] !== onApplyFix) {
    let t8;
    if ($[15] !== appliedFields || $[16] !== handleAppliedChange || $[17] !== onApplyFix) {
      t8 = field => <ErrorFieldEditor key={field.fieldName} field={field} onApplyFix={onApplyFix} onAppliedChange={handleAppliedChange} initialApplied={appliedFields.has(field.fieldName)} />;
      $[15] = appliedFields;
      $[16] = handleAppliedChange;
      $[17] = onApplyFix;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    t7 = fields.map(t8);
    $[10] = appliedFields;
    $[11] = fields;
    $[12] = handleAppliedChange;
    $[13] = onApplyFix;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[19] !== t7) {
    t8 = <div className="space-y-2">{t7}</div>;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== t3 || $[22] !== t6 || $[23] !== t8) {
    t9 = <div className={t3}>{t6}{t8}</div>;
    $[21] = t3;
    $[22] = t6;
    $[23] = t8;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  return t9;
}